import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { loadSectionTexts, loadScheduleItems } from '../../utils/contentService';
import LocationModal from './LocationModal';

export default function Footer() {
  const { i18n } = useTranslation();
  const [texts, setTexts] = useState({});
  const [infoTexts, setInfoTexts] = useState({});
  const [ceremonyModalData, setCeremonyModalData] = useState(null);
  const [dinnerModalData, setDinnerModalData] = useState(null);
  const [activeModalData, setActiveModalData] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    setTexts(loadSectionTexts('footer', i18n.language));
    setInfoTexts(loadSectionTexts('info', i18n.language));
    
    const fetchSchedule = async () => {
      try {
        const items = await loadScheduleItems(i18n.language);
        
        const ceremonyItem = items.find(item => item.id === 'ceremony');
        if (ceremonyItem) {
          setCeremonyModalData({
            title: ceremonyItem.locationLink,
            details: ceremonyItem.locationDetails,
            mapUrl: ceremonyItem.locationMapUrl
          });
        }

        const dinnerItem = items.find(item => item.id === 'dinner');
        if (dinnerItem) {
          setDinnerModalData({
            title: dinnerItem.locationLink,
            details: dinnerItem.locationDetails,
            mapUrl: dinnerItem.locationMapUrl
          });
        }
      } catch (error) {
        console.error("Failed to load schedule items:", error);
      }
    };
    
    fetchSchedule();
  }, [i18n.language]);

  const openModal = (data) => {
    if (data) {
      setActiveModalData(data);
      setIsModalOpen(true);
    }
  };

  return (
    <footer className="w-full py-6 lg:py-8 bg-test">
      <LocationModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={activeModalData?.title}
        details={activeModalData?.details}
        mapUrl={activeModalData?.mapUrl}
        mapButtonText={infoTexts?.mapButtonSchedule || "Google Maps"}
        closeText={infoTexts?.close || "✕"}
      />

      <div className="w-full px-4 lg:px-8">
        {/* Separator Line */}
        <div className="border-t border-black/40 mb-6 lg:mb-8"></div>

        <div className="flex flex-col lg:flex-row justify-between items-center gap-4 lg:gap-4">

          {/* LEFT: Main Info (Title) */}
          <div className="order-1 text-center lg:text-left max-w-md">
            <h2 className="text-[13px] lg:text-xl font-normal font-serif italic text-black leading-tight mb-1">
              {texts.names || ""} <br />
              <span className="text-[13px] lg:text-base opacity-80">{texts.date || ""}</span>
            </h2>
            <p className="text-[13px] lg:text-sm font-normal font-serif italic text-black opacity-70 mt-2 lg:mt-1 lg:hidden xl:block">
              {texts.tagline || ""}
            </p>
          </div>

          {/* CENTER: Locations Details */}
          <div className="order-2 flex flex-col md:flex-row gap-4 lg:gap-12 text-center lg:text-left">
            {/* Ceremony */}
            <div className="space-y-0.5 lg:space-y-1">
              <h3 className="text-[15px] lg:text-base font-bold font-serif italic text-black">
                📍 {texts.ceremony || "Ceremony:"}
              </h3>
              <p 
                onClick={() => openModal(ceremonyModalData)}
                className="text-[13px] lg:text-sm font-normal font-serif italic text-black opacity-80 whitespace-normal lg:whitespace-nowrap underline cursor-pointer hover:opacity-60 transition-opacity"
              >
                {texts.ceremonyLocation || "Agios Vasilios Chapel"}
              </p>
            </div>

            {/* Dinner */}
            <div className="space-y-0.5 lg:space-y-1">
              <h3 className="text-[15px] lg:text-base font-bold font-serif italic text-black">
                📍 {texts.dinner || "Dinner:"}
              </h3>
              <p 
                onClick={() => openModal(dinnerModalData)}
                className="text-[13px] lg:text-sm font-normal font-serif italic text-black opacity-80 whitespace-normal lg:whitespace-nowrap underline cursor-pointer hover:opacity-60 transition-opacity"
              >
                {texts.dinnerLocation || "Axivadaki tavern"}
              </p>
            </div>
          </div>

        </div>

        {/* BOTTOM: Copyright */}
        <div className="text-center pt-4 lg:pt-6 mt-2">
          <p className="text-[12px] lg:text-xs font-light font-serif italic text-black opacity-50">
            {texts.copyright || ""}
          </p>
        </div>
      </div>
    </footer>
  );
}
